import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar, Save, Loader2, CheckCircle2, Clock, Store, Timer, ClipboardCheck,
  Lock, Sparkles, Wallet, SlidersHorizontal, Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { SeloBeta, AvisoBeta } from "@/components/BadgeBeta";
import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

// Opções de recuo do primeiro/último horário em relação à abertura/fechamento.
// Valor em minutos — persiste como setting no backend (v1.5.373+).
const OFFSET_OPTIONS_INI = [
  { value: 0, label: "Na abertura" },
  { value: 30, label: "30 min depois de abrir" },
  { value: 60, label: "1h depois de abrir" },
  { value: 90, label: "1h30 depois de abrir" },
  { value: 120, label: "2h depois de abrir" },
];
const OFFSET_OPTIONS_FIM = [
  { value: 0, label: "Até o fechamento" },
  { value: 30, label: "30 min antes de fechar" },
  { value: 60, label: "1h antes de fechar" },
  { value: 90, label: "1h30 antes de fechar" },
  { value: 120, label: "2h antes de fechar" },
];

// Quantos dias ADIANTE de hoje o cliente pode agendar (0 = só hoje). v1.5.374+
const JANELA_OPTIONS = [
  { value: 0, label: "Só hoje" },
  { value: 1, label: "Hoje + 1 dia" },
  { value: 2, label: "Hoje + 2 dias" },
  { value: 3, label: "Hoje + 3 dias" },
  { value: 4, label: "Hoje + 4 dias" },
  { value: 5, label: "Hoje + 5 dias" },
  { value: 6, label: "Hoje + 6 dias (padrão)" },
  { value: 7, label: "Hoje + 7 dias" },
  { value: 10, label: "Hoje + 10 dias" },
  { value: 14, label: "Hoje + 14 dias" },
  { value: 21, label: "Hoje + 21 dias" },
  { value: 30, label: "Hoje + 30 dias" },
];

// Intervalo entre horários oferecidos. v1.5.374+
const SLOT_OPTIONS = [
  { value: 15, label: "De 15 em 15 min" },
  { value: 30, label: "De 30 em 30 min (padrão)" },
  { value: 45, label: "De 45 em 45 min" },
  { value: 60, label: "De 1h em 1h" },
];

export default function AgendamentosConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    ativo: false,
    janela_dias_adiante: 6,
    slot_minutos: 30,
    // Fail-CLOSED: se o GET de config falhar (rede/sessão), a página nasce
    // travada — o backend é quem destrava via disabled:false. Evita UI
    // interativa com lock ativo no servidor (achado do review v1.5.380).
    disabled: true,
    formas_pagamento: [],
    primeiro_slot_offset_min: 0,
    ultimo_slot_offset_min: 0,
    max_por_slot: 0,
  });
  // Formas de pagamento disponíveis no tenant (fetch de /api/payment-methods)
  const [paymentMethodsAll, setPaymentMethodsAll] = useState([]);

  useEffect(() => {
    // Fetch em paralelo — precisamos das duas listas pra decidir se
    // pré-marcamos tudo (primeiro acesso) ou respeitamos config salva.
    Promise.all([fetchConfig(), fetchPaymentMethods()])
      .then(([cfgRaw, methods]) => {
        if (!cfgRaw) return;
        // Se `formas_pagamento` veio NULL do backend, é primeiro acesso:
        // pré-marca TODAS as formas ativas (esse é o default esperado).
        // Caso contrário respeita a lista salva (inclusive [] = "nada aceito").
        const isFirstTime = cfgRaw.formas_pagamento === null || cfgRaw.formas_pagamento === undefined;
        const initialFormas = isFirstTime
          ? methods.map((m) => m.code || m.id).filter(Boolean)
          : cfgRaw.formas_pagamento;
        setConfig({
          ativo: !!cfgRaw.ativo,
          // janela_dias_adiante existe desde v1.5.374; fallback deriva do
          // janela_dias_max antigo (dias exibidos contando hoje).
          janela_dias_adiante: cfgRaw.janela_dias_adiante ?? Math.max(0, (cfgRaw.janela_dias_max || 7) - 1),
          slot_minutos: Number(cfgRaw.slot_minutos) || 30,
          disabled: !!cfgRaw.disabled,
          formas_pagamento: initialFormas,
          primeiro_slot_offset_min: Number(cfgRaw.primeiro_slot_offset_min) || 0,
          ultimo_slot_offset_min: Number(cfgRaw.ultimo_slot_offset_min) || 0,
          max_por_slot: Number(cfgRaw.max_por_slot) || 0,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/agendamentos/config", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data || null;
    } catch (error) {
      console.error("Erro ao buscar configurações de agendamento:", error);
      return null;
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/payment-methods", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const active = Array.isArray(res.data) ? res.data.filter((m) => m.enabled !== false) : [];
      setPaymentMethodsAll(active);
      return active;
    } catch (error) {
      console.error("Erro ao buscar formas de pagamento:", error);
      return [];
    }
  };

  const toggleFormaPagamento = (code) => {
    setConfig((c) => {
      const cur = new Set(c.formas_pagamento || []);
      if (cur.has(code)) cur.delete(code);
      else cur.add(code);
      return { ...c, formas_pagamento: [...cur] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        "/api/agendamentos/config",
        {
          ativo: config.ativo,
          formas_pagamento: config.formas_pagamento || [],
          primeiro_slot_offset_min: config.primeiro_slot_offset_min || 0,
          ultimo_slot_offset_min: config.ultimo_slot_offset_min || 0,
          max_por_slot: config.max_por_slot || 0,
          janela_dias_adiante: config.janela_dias_adiante ?? 6,
          slot_minutos: config.slot_minutos || 30,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        config.ativo
          ? "Agendamentos ativados! O cardápio já mostra a opção para clientes quando você estiver fechado."
          : "Agendamentos desativados.",
      );
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  // Cabeçalho fixo: aparece na hora, mesmo enquanto a configuração carrega.
  const cabecalho = (
      <div className="flex flex-col items-center text-center gap-3 pt-2">
        <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-md">
          <Calendar className="w-7 h-7 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-bold inline-flex items-center gap-2">
              Agendamentos
              <SeloBeta />
            </h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Aceite pedidos mesmo quando a loja está fechada — o cliente escolhe o dia e o horário.
          </p>
        </div>
      </div>
  );

  // Carregamento com cards (25/10/2026): na 1ª abertura os cartões piscam no
  // lugar do spinner — nunca mostra o interruptor desligado e os valores padrão
  // antes de saber o que a loja gravou. Salvar não volta para cá.
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {cabecalho}
        <CarregandoBloco altura="h-24" />
        <CarregandoFormulario campos={3} />
        <CarregandoFormulario campos={4} />
        <CarregandoBloco altura="h-40" />
      </div>
    );
  }

  const locked = !!config.disabled;

  const handleLockedClick = () => {
    toast.info("Agendamentos está em desenvolvimento. Em breve disponível!", {
      description: "Estamos finalizando os últimos ajustes antes de liberar pra todo mundo.",
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header centralizado */}
      {cabecalho}

      {/* Aviso de BETA — recurso liberado (25/08/2026) mas ainda em ajuste. So'
          aparece quando NAO esta travado: se o kill-switch voltar, quem fala e'
          o card de "Em breve" abaixo. Sao mensagens diferentes — uma diz
          "travado", a outra diz "liberado, mas novo". */}
      {!locked && (
        <AvisoBeta>
          Agendamentos está em <strong className="text-foreground">beta</strong>.
          Já funciona de ponta a ponta: o cliente escolhe dia e horário mesmo com
          a loja fechada e o pedido cai aqui pra você aprovar. Comece com uma
          janela curta de dias e poucos pedidos por horário até pegar o ritmo —
          esses limites você ajusta abaixo a qualquer momento.
        </AvisoBeta>
      )}

      {/* Card EM BREVE — só aparece quando o recurso está travado (feature flag global) */}
      {locked && (
        <div className="rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 relative overflow-hidden">
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-black uppercase tracking-wider shadow-md">
              <Sparkles className="w-3 h-3" />
              Em breve
            </span>
          </div>
          <div className="flex items-start gap-4 pr-16">
            <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 shadow-md">
              <Lock className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg leading-tight">Recurso em desenvolvimento</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Estamos finalizando os últimos ajustes de <strong>Agendamentos</strong> antes de liberar pra todo mundo.
                Em breve você vai poder ativar aqui e receber pedidos mesmo com a loja fechada.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Enquanto isso, a opção fica travada — sem risco de o cliente agendar sem você conseguir aprovar.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Card: switch principal — desabilitado enquanto locked */}
      <Card className={locked ? "opacity-60" : ""}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Aceitar pedidos agendados
              </CardTitle>
              <CardDescription className="mt-1">
                Quando ligado, o cliente vê a opção de agendar entrega ou retirada no cardápio público, mesmo com a loja fechada.
              </CardDescription>
            </div>
            <div onClick={locked ? handleLockedClick : undefined} className={locked ? "cursor-not-allowed" : ""}>
              <Switch
                checked={config.ativo}
                onCheckedChange={locked ? undefined : ((v) => setConfig((c) => ({ ...c, ativo: v })))}
                disabled={locked}
                aria-label="Ativar agendamentos"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tiles-resumo (refletem a configuração ao vivo) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoTile
              icon={<Calendar className="w-4 h-4" />}
              label="Janela de dias"
              value={config.janela_dias_adiante === 0 ? "Só hoje" : `Hoje + ${config.janela_dias_adiante} dia${config.janela_dias_adiante > 1 ? "s" : ""}`}
            />
            <InfoTile
              icon={<CheckCircle2 className="w-4 h-4" />}
              label="Horários"
              value={config.slot_minutos === 60 ? "De 1h em 1h" : `De ${config.slot_minutos} em ${config.slot_minutos} min`}
            />
          </div>

          {/* "Como funciona" — features com ícone + heading + descrição */}
          <div className="rounded-xl border bg-muted/30 p-5">
            <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-black">?</span>
              Como funciona
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FeatureRow
                icon={<Store className="w-5 h-5" />}
                title="Só com loja fechada"
                desc="A opção só aparece pro cliente quando você está fora do horário ou fechou manualmente."
              />
              <FeatureRow
                icon={<Clock className="w-5 h-5" />}
                title="Respeita seu horário"
                desc="Os horários oferecidos vêm do seu horário de funcionamento. Abre às 18h? Primeiro slot é 18h."
              />
              <FeatureRow
                icon={<Timer className="w-5 h-5" />}
                title="Horários no seu ritmo"
                desc="Você escolhe o intervalo entre horários (15 min a 1h) e até quantos dias adiante aceita agendar."
              />
              <FeatureRow
                icon={<ClipboardCheck className="w-5 h-5" />}
                title="Você aprova cada pedido"
                desc={"Pela aba \"Agendados\" no painel de Pedidos. O cupom só imprime quando você aprovar."}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card: janela de horários + limite por horário (v1.5.373+) */}
      <Card className={locked ? "opacity-60" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
            Janela de horários e limite
          </CardTitle>
          <CardDescription className="mt-1">
            Controle quando começam e terminam os horários oferecidos — e quantos agendamentos cada horário aceita.
            Evita acúmulo de pedidos em horários que a cozinha ainda não dá conta.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Janela de dias + intervalo entre horários (v1.5.374+) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Agendar até quantos dias adiante</Label>
              <div onClick={locked ? handleLockedClick : undefined}>
                <Select
                  value={String(config.janela_dias_adiante)}
                  onValueChange={locked ? undefined : ((v) => setConfig((c) => ({ ...c, janela_dias_adiante: Number(v) })))}
                  disabled={locked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JANELA_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {config.janela_dias_adiante === 0
                  ? "Cliente só agenda pra hoje."
                  : `Cliente agenda de hoje até ${config.janela_dias_adiante} dia${config.janela_dias_adiante > 1 ? "s" : ""} pra frente.`}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Intervalo entre horários</Label>
              <div onClick={locked ? handleLockedClick : undefined}>
                <Select
                  value={String(config.slot_minutos)}
                  onValueChange={locked ? undefined : ((v) => setConfig((c) => ({ ...c, slot_minutos: Number(v) })))}
                  disabled={locked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLOT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const s = config.slot_minutos;
                  const seq = [0, s, 2 * s].map((m) => {
                    const total = 18 * 60 + m;
                    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
                  });
                  return `Ex.: abrindo às 18h → ${seq.join(", ")}…`;
                })()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Primeiro horário do dia</Label>
              <div onClick={locked ? handleLockedClick : undefined}>
                <Select
                  value={String(config.primeiro_slot_offset_min)}
                  onValueChange={locked ? undefined : ((v) => setConfig((c) => ({ ...c, primeiro_slot_offset_min: Number(v) })))}
                  disabled={locked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OFFSET_OPTIONS_INI.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const m = 18 * 60 + (config.primeiro_slot_offset_min || 0);
                  const hh = String(Math.floor(m / 60)).padStart(2, "0");
                  const mm = String(m % 60).padStart(2, "0");
                  return `Ex.: loja abre às 18h → primeiro horário oferecido: ${hh}:${mm}`;
                })()}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Último horário do dia</Label>
              <div onClick={locked ? handleLockedClick : undefined}>
                <Select
                  value={String(config.ultimo_slot_offset_min)}
                  onValueChange={locked ? undefined : ((v) => setConfig((c) => ({ ...c, ultimo_slot_offset_min: Number(v) })))}
                  disabled={locked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OFFSET_OPTIONS_FIM.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  // Último slot = fechamento - offset - intervalo (o slot do horário
                  // exato de fechar nunca é oferecido — a cozinha já está fechando).
                  const m = 23 * 60 - (config.ultimo_slot_offset_min || 0) - (config.slot_minutos || 30);
                  const hh = String(Math.floor(m / 60)).padStart(2, "0");
                  const mm = String(m % 60).padStart(2, "0");
                  return `Ex.: loja fecha às 23h → último horário oferecido: ${hh}:${mm}`;
                })()}
              </p>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Limite de agendamentos por horário
            </Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={0}
                max={999}
                value={config.max_por_slot === 0 ? "" : config.max_por_slot}
                placeholder="Ilimitado"
                onClick={locked ? handleLockedClick : undefined}
                onChange={locked ? undefined : ((e) => {
                  const v = e.target.value === "" ? 0 : Math.max(0, Math.min(999, parseInt(e.target.value, 10) || 0));
                  setConfig((c) => ({ ...c, max_por_slot: v }));
                })}
                disabled={locked}
                className="w-36"
              />
              {config.max_por_slot > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={locked ? handleLockedClick : (() => setConfig((c) => ({ ...c, max_por_slot: 0 })))}
                  disabled={locked}
                >
                  Voltar pra ilimitado
                </Button>
              )}
            </div>
            {config.max_por_slot === 0 ? (
              <p className="text-xs text-green-600 dark:text-green-400">
                ✅ Sem limite — cada horário aceita quantos agendamentos chegarem.
              </p>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                ⚠️ Cada horário aceita no máximo <strong>{config.max_por_slot}</strong> agendamento{config.max_por_slot > 1 ? "s" : ""}.
                Horários lotados somem do cardápio automaticamente.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Card: formas de pagamento aceitas em agendamentos */}
      <Card className={locked ? "opacity-60" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            Formas de pagamento aceitas em agendamentos
          </CardTitle>
          <CardDescription className="mt-1">
            Marque as formas que aparecem pro cliente na hora de agendar. Formas <strong>desmarcadas</strong> ficam escondidas.
            Marque só <strong>PIX Online</strong>, por exemplo, se quiser garantir pagamento antecipado antes de bloquear o horário.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentMethodsAll.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              Nenhuma forma de pagamento ativa cadastrada.
              <br />
              Configure em <strong>Financeiro → Formas de Pagamento</strong>.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {paymentMethodsAll.map((m) => {
                const code = m.code || m.id;
                const checked = (config.formas_pagamento || []).includes(code);
                return (
                  <label
                    key={code}
                    onClick={(e) => { if (locked) { e.preventDefault(); handleLockedClick(); } }}
                    className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${checked ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40"} ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={locked ? undefined : () => toggleFormaPagamento(code)}
                      disabled={locked}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm leading-tight truncate">{m.label || m.name || code}</p>
                      {m.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{m.description}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          {paymentMethodsAll.length > 0 && (() => {
            const selected = (config.formas_pagamento || []).length;
            const total = paymentMethodsAll.length;
            if (selected === 0) {
              return (
                <p className="text-xs text-red-600 dark:text-red-400 mt-3 font-semibold">
                  ⛔ Nenhuma forma marcada — clientes NÃO vão conseguir agendar. Marque pelo menos uma.
                </p>
              );
            }
            if (selected === total) {
              return (
                <p className="text-xs text-green-600 dark:text-green-400 mt-3">
                  ✅ Todas as formas ativas são aceitas em agendamentos.
                </p>
              );
            }
            return (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                ⚠️ {selected} de {total} formas aceitas — as demais NÃO aparecem no cardápio ao agendar.
              </p>
            );
          })()}
        </CardContent>
      </Card>

      {/* Botão salvar — travado enquanto locked */}
      <div className="flex justify-end">
        <Button
          onClick={locked ? handleLockedClick : handleSave}
          disabled={saving || locked}
          className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : locked ? (
            <Lock className="w-4 h-4" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {locked ? "Em breve" : "Salvar alterações"}
        </Button>
      </div>
    </div>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <div className="rounded-lg border p-3 bg-card">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <p className="mt-1 font-bold text-sm">{value}</p>
    </div>
  );
}

function FeatureRow({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-sm leading-tight">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
